import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { notesAPI } from '../../api/notesApi';
import { useAuth } from '../../contexts/AuthContext';
import { isNotesAdmin } from '../../constants/notes';
import {
    ClockIcon,
    PlusIcon
} from '@heroicons/react/24/outline';
import ActionToolbar from '../../components/ui/ActionToolbar';
import IconActionButton from '../../components/ui/IconActionButton';

function groupRootNotesByOwner(notes) {
    const m = new Map();
    for (const n of notes) {
        const oid = n.owner;
        const label = n.owner_details?.full_name || n.owner_details?.email || `User ${oid}`;
        if (!m.has(oid)) m.set(oid, { ownerId: oid, label, notes: [] });
        m.get(oid).notes.push(n);
    }
    return Array.from(m.values()).sort((a, b) => a.label.localeCompare(b.label));
}

const NotesHome = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [recentNotes, setRecentNotes] = useState([]);
    const [ownerGroups, setOwnerGroups] = useState(null);
    const [greeting, setGreeting] = useState('');

    const getGreeting = () => {
        const hour = new Date().getHours();
        if (hour < 12) return 'Good morning';
        if (hour < 18) return 'Good afternoon';
        return 'Good evening';
    };

    const loadRecentNotes = useCallback(async () => {
        try {
            const response = await notesAPI.getNotes(null);
            const backendNotes = Array.isArray(response) ? response : (response?.results || []);
            const backendNoteIds = new Set(backendNotes.map(n => n.id));

            const localRecents = JSON.parse(localStorage.getItem('recent_notes') || '[]');
            const validRecents = localRecents.filter(r => backendNoteIds.has(r.id));

            if (validRecents.length !== localRecents.length) {
                localStorage.setItem('recent_notes', JSON.stringify(validRecents));
            }

            if (isNotesAdmin(user)) {
                const sorted = [...backendNotes].sort(
                    (a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at)
                );
                setOwnerGroups(groupRootNotesByOwner(sorted));
                if (validRecents.length > 0) {
                    setRecentNotes(validRecents.slice(0, 8));
                } else {
                    setRecentNotes(sorted.slice(0, 8));
                }
                return;
            }

            setOwnerGroups(null);

            if (validRecents.length > 0) {
                setRecentNotes(validRecents.slice(0, 8));
            } else {
                const sorted = backendNotes
                    .sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at))
                    .slice(0, 8);
                setRecentNotes(sorted);
            }
        } catch (error) {
            console.error("Failed to load recent notes", error);
            setOwnerGroups(null);
            const localRecents = JSON.parse(localStorage.getItem('recent_notes') || '[]');
            setRecentNotes(localRecents.slice(0, 8));
        }
    }, [user]);

    useEffect(() => {
        setGreeting(getGreeting());
        loadRecentNotes();
    }, [loadRecentNotes]);

    const handleCreateNote = async () => {
        try {
            const newNote = await notesAPI.createNote({
                title: 'Untitled',
                parent: null
            });
            navigate(`/notes/${newNote.id}`);
        } catch (error) {
            console.error("Failed to create note", error);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    return (
        <div className="max-w-6xl mx-auto px-8 py-16 h-full overflow-y-auto">
            <div className="text-center mb-12">
                <h1 className="text-4xl font-bold text-gray-800 dark:text-gray-100">
                    {greeting}
                </h1>
            </div>

            <ActionToolbar className="mb-6">
                <IconActionButton
                    icon={PlusIcon}
                    title="Yeni Not"
                    onClick={handleCreateNote}
                />
            </ActionToolbar>

            <div>
                <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-4 flex items-center pl-1">
                    <ClockIcon className="w-4 h-4 mr-2" />
                    {isNotesAdmin(user) ? 'All notes (by creator)' : 'Recently visited'}
                </h2>

                {isNotesAdmin(user) && ownerGroups && ownerGroups.length > 0 ? (
                    <div className="space-y-10">
                        {ownerGroups.map((group) => (
                            <div key={group.ownerId}>
                                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 pl-1 border-b border-gray-200 dark:border-gray-700 pb-2">
                                    {group.label}
                                </h3>
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                                    {group.notes.map(note => (
                                        <div
                                            key={note.id}
                                            onClick={() => navigate(`/notes/${note.id}`)}
                                            className="group cursor-pointer bg-white dark:bg-[#202020] hover:bg-gray-50 dark:hover:bg-[#2C2C2C] border border-gray-200 dark:border-transparent rounded-xl p-4 shadow-sm transition-all flex flex-col h-32 relative text-left"
                                        >
                                            <div className="flex items-start justify-between mb-3">
                                                <span className="text-2xl opacity-80">{note.icon || '📄'}</span>
                                            </div>
                                            <h3 className="font-semibold text-gray-800 dark:text-gray-200 text-sm line-clamp-2 mb-auto pr-2">
                                                {note.title || 'Untitled'}
                                            </h3>
                                            <div className="text-[10px] text-gray-400 mt-2 font-medium">
                                                {formatDate(note.updated_at || note.created_at)}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                            <button
                                onClick={handleCreateNote}
                                className="group flex flex-col h-32 bg-transparent border border-dashed border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-[#252525] hover:border-gray-400 dark:border-gray-600 rounded-xl p-4 transition-all text-left"
                            >
                                <div className="flex-1">
                                    <div className="w-8 h-8 rounded border border-gray-300 dark:border-gray-700 flex items-center justify-center text-gray-400 mb-2 group-hover:border-gray-400 group-hover:text-gray-500 transition-colors">
                                        <PlusIcon className="w-4 h-4" />
                                    </div>
                                </div>
                                <span className="text-sm font-medium text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors">
                                    New page
                                </span>
                            </button>
                        </div>
                    </div>
                ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                    {recentNotes.map(note => (
                        <div
                            key={note.id}
                            onClick={() => navigate(`/notes/${note.id}`)}
                            className="group cursor-pointer bg-white dark:bg-[#202020] hover:bg-gray-50 dark:hover:bg-[#2C2C2C] border border-gray-200 dark:border-transparent rounded-xl p-4 shadow-sm transition-all flex flex-col h-32 relative text-left"
                        >
                            <div className="flex items-start justify-between mb-3">
                                <span className="text-2xl opacity-80">{note.icon || '📄'}</span>
                            </div>
                            <h3 className="font-semibold text-gray-800 dark:text-gray-200 text-sm line-clamp-2 mb-auto pr-2">
                                {note.title || 'Untitled'}
                            </h3>
                            <div className="text-[10px] text-gray-400 mt-2 font-medium flex items-center">
                                <div className="w-4 h-4 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-[8px] mr-1.5 text-gray-600 dark:text-gray-300">
                                    B
                                </div>
                                {formatDate(note.updated_at || note.created_at)}
                            </div>
                        </div>
                    ))}

                    <button
                        onClick={handleCreateNote}
                        className="group flex flex-col h-32 bg-transparent border border-dashed border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-[#252525] hover:border-gray-400 dark:border-gray-600 rounded-xl p-4 transition-all text-left"
                    >
                        <div className="flex-1">
                            <div className="w-8 h-8 rounded border border-gray-300 dark:border-gray-700 flex items-center justify-center text-gray-400 mb-2 group-hover:border-gray-400 group-hover:text-gray-500 transition-colors">
                                <PlusIcon className="w-4 h-4" />
                            </div>
                        </div>
                        <span className="text-sm font-medium text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors">
                            New page
                        </span>
                    </button>
                </div>
                )}
            </div>
        </div>
    );
};

export default NotesHome;
